import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { graphql, useFragment } from "react-relay";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { useTakeoverBottomAdSlot } from "scmp-app/components/advertisement/takeover-ad-slot/hooks";
import { AfterPageLayout } from "scmp-app/components/after-page-layout";
import { ArticleImageGallery } from "scmp-app/components/article/article-image-gallery";
import { ImageSize } from "scmp-app/components/article/article-leading/article-leading-images/enums";
import type { ArticleRenderChildProps } from "scmp-app/components/article/article-render";
import { GlobalChecker } from "scmp-app/components/article/article-render/global-checker";
import { useMobileInlineAdSlotIsDisabledFlag } from "scmp-app/components/article/article-render/hooks";
import type { InlineAdSlotRenderProps } from "scmp-app/components/schema-render/common/inline-ad-slot/hooks";
import { MagazinesStyleVariant as variant } from "scmp-app/components/section/section-style/const";
import { StoryCardCarouselWidget } from "scmp-app/components/section/section-style/section-style-story-card-carousel-widget";
import { useCurrentArticleIds } from "scmp-app/lib/current-item";
import type { styleArticleContent$key } from "scmp-app/queries/__generated__/styleArticleContent.graphql";

import { ArticleMoreOnThisList } from "./article-more-on-this-list";
import { StyleArticleBlockquoteQuote } from "./blockquote-quote";
import {
  ActionWidget,
  Author,
  AuthorDetail,
  Content,
  DesktopBeforeContentAdSlot,
  DesktopHeaderAdSlot,
  Follow,
  Headline,
  Leading,
  MobileUpBeforeContentAdSlot,
  MoreOnThis,
  Section,
  ShareWidget,
  Social,
  StyleArticleContainer,
  SubHeadline,
  Topic,
  TopicSocialContainer,
} from "./styles";

export type Props = ArticleRenderChildProps & {
  reference: styleArticleContent$key;
};

export const StyleArticle: FunctionComponent<Props> = ({ provider, reference }) => {
  const article = useFragment(
    graphql`
      fragment styleArticleContent on Content {
        entityId
        ...articleImageGalleryContent
        ...articleMoreOnThisListStyleArticle
        ...globalCheckerContent
        ...styleArticleAuthorBlockArticle
      }
    `,
    reference,
  );

  const isInlineAdSlotDisabled = useMobileInlineAdSlotIsDisabledFlag();
  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"), false);
  const inlineAdSlots = useMemo<InlineAdSlotRenderProps>(
    () =>
      isDesktopUp
        ? {
            0: {
              adUnit: "d_banner5",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [
                [970, 250],
                [300, 250],
                [1, 1],
              ],
            },
            1: {
              adUnit: "outstream1",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [
                [300, 250],
                [640, 360],
                [640, 480],
                [480, 360],
                [480, 270],
                [1, 1],
              ],
            },
            2: {
              adUnit: "d_banner2",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250]],
            },
            3: {
              adUnit: "d_lrec2",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[300, 250]],
            },
            4: {
              adUnit: "d_banner4",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250]],
            },
            5: {
              adUnit: "d_banner4",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250]],
            },
            6: {
              adUnit: "d_banner4",
              breakpoint: theme.breakpoints.up("desktop"),
              sizes: [[970, 250]],
            },
            7: {
              adUnit: "native4",
              breakpoint: theme.breakpoints.up("desktop"),
            },
          }
        : {
            0: {
              adUnit: "m_lrec1",
              breakpoint: theme.breakpoints.down("desktop"),
              sizes: [
                [300, 250],
                [320, 480],
              ],
            },
            1: {
              adUnit: "outstream1",
              breakpoint: theme.breakpoints.down("desktop"),
              sizes: [
                [300, 250],
                [320, 180],
                [1, 1],
              ],
            },
            2: {
              adUnit: "m_banner5",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [
                [320, 100],
                [300, 250],
                [1, 1],
              ],
            },
            3: {
              adUnit: "m_lrec2",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [
                [300, 250],
                [320, 480],
              ],
            },
            4: {
              adUnit: "m_lrec4d",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [
                [300, 250],
                [320, 480],
              ],
            },
            5: {
              adUnit: "m_lrec4e",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [
                [300, 250],
                [320, 480],
              ],
            },
            6: {
              adUnit: "m_lrec4f",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
              sizes: [
                [300, 250],
                [320, 480],
              ],
            },
            7: {
              adUnit: "native4",
              breakpoint: theme.breakpoints.down("desktop"),
              isDisabled: isInlineAdSlotDisabled,
            },
          },
    [isDesktopUp, isInlineAdSlotDisabled],
  );

  const { isEnableTakeoverBottomAdSlot } = useTakeoverBottomAdSlot({
    variant,
  });

  const { currentArticleId } = useCurrentArticleIds();
  return (
    <StyleArticleContainer>
      <DesktopHeaderAdSlot>
        {provider.adslot({
          adUnit: "d_banner1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [
            [970, 250],
            [1, 1],
            [480, 360],
            [480, 270],
          ],
        })}
      </DesktopHeaderAdSlot>
      <DesktopBeforeContentAdSlot>
        {provider.adslot({
          adUnit: "d_lrec1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.up("desktop"),
          sizes: [[300, 250]],
          withLabel: true,
        })}
      </DesktopBeforeContentAdSlot>
      <MobileUpBeforeContentAdSlot>
        {provider.adslot({
          adUnit: "m_banner1",
          autoRefreshOptions: {
            scrollBackRefreshOptions: {
              slotArticleEntityId: article.entityId,
            },
          },
          breakpoint: theme.breakpoints.down("desktop"),
          sizes: [[300, 250]],
          withLabel: true,
        })}
      </MobileUpBeforeContentAdSlot>
      <Section>
        {provider.sections({
          variant,
        })}
      </Section>
      <Headline>{provider.headline({ variant })}</Headline>
      <Author reference={article} />
      <ArticleImageGallery reference={article}>
        <Leading>
          {provider.leading({
            advertising: {
              instream: "instream3",
            },
            responsiveDescriptionVariants: {
              desktopUp: "show",
              mobileUp: "show",
              tabletUp: "show",
            },
            responsiveImageVariants: {
              desktopUp: "normal",
              mobileUp: "square",
              tabletUp: "normal",
            },
            responsiveVariants: {
              desktopUp: ImageSize["1676x943"],
              mobileUp: ImageSize["768x768"],
              tabletUp: ImageSize["1676x943"],
            },
            withExternalGallery: true,
          })}
        </Leading>

        <TopicSocialContainer>
          <Follow>{provider.topicFollowWidget({ variant })}</Follow>
          <Social>
            <ShareWidget>{provider.shareWidget({ variant })}</ShareWidget>
            <ActionWidget>{provider.actionWidget({ variant })}</ActionWidget>
          </Social>
        </TopicSocialContainer>

        <SubHeadline>{provider.subHeadline({ variant })}</SubHeadline>

        <Content>
          {provider.body({
            extraComponentMap: {
              "blockquote-quote": StyleArticleBlockquoteQuote,
            },
            inlineAdSlots,
            isDisabledArticleImageGallery: true,
          })}
        </Content>
      </ArticleImageGallery>
      <ClientSideSuspense>
        <GlobalChecker reference={article}>
          <Topic>
            {provider.topics({
              label: "TAGS",
              variant,
              withFollow: false,
              withSeparator: false,
            })}
          </Topic>
          <AuthorDetail>{provider.authorDetails({ variant })}</AuthorDetail>
          <MoreOnThis>
            <ArticleMoreOnThisList reference={article} />
          </MoreOnThis>
        </GlobalChecker>
      </ClientSideSuspense>
      <AfterPageLayout>
        {currentArticleId === article.entityId && (
          <ClientSideSuspense>
            {isEnableTakeoverBottomAdSlot ? (
              provider.takeOverAdSlot({ variant })
            ) : (
              <StoryCardCarouselWidget />
            )}
          </ClientSideSuspense>
        )}
      </AfterPageLayout>
    </StyleArticleContainer>
  );
};

StyleArticle.displayName = "StyleArticle";
